import { getBlankUnitMeta } from "./fillQuestionUtils";

// Answer-unit chip next to the typed box (W, Watts, cals, RF, feet, £…). Populated by the API from
// ExtractsAnswerUnit / AnswerUnitVocabulary — the take pages only display it. Kept here rather than
// copied per page: the two copies had already drifted apart on compound support.
export const resolveAnswerUnitProps = (question, enabled) => {
  if (!enabled) return { unitSuffix: undefined, unitPrefix: undefined };
  const unit = question?.answer_data?.answer_unit;
  if (!unit) return { unitSuffix: undefined, unitPrefix: undefined };
  const isPrefix = question?.answer_data?.answer_unit_prefix === true;
  return {
    unitSuffix: isPrefix ? undefined : unit,
    unitPrefix: isPrefix ? unit : undefined,
  };
};

export const resolveBlankOrAnswerUnitProps = (question, enabled, blankIdx = 0) => {
  if (!enabled) return { unitSuffix: undefined, unitPrefix: undefined };
  const u = getBlankUnitMeta(question, blankIdx);
  if (u.unit) {
    return {
      unitSuffix: u.prefix ? undefined : u.unit,
      unitPrefix: u.prefix ? u.unit : undefined,
    };
  }
  return resolveAnswerUnitProps(question, enabled);
};

// "6 minutes 24 seconds" wears two units, so it gets one numeric box per unit rather than a single
// box with one chip. Returns null unless there are at least two.
export const resolveCompoundUnits = (question, enabled) => {
  if (!enabled) return null;
  const units = question?.answer_data?.answer_compound_units;
  return Array.isArray(units) && units.length > 1 ? units : null;
};

// A currency member of a compound is written before its value ("£22 60p"), not after it.
export const CURRENCY_UNITS = ["£", "$", "€", "¥"];

const escapeUnit = (u) => String(u).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const matchUnitValue = (segment, unit) => {
  const u = escapeUnit(unit);
  const m = CURRENCY_UNITS.includes(unit)
    ? String(segment ?? "").match(new RegExp(`${u}\\s*(\\d+(?:\\.\\d+)?)`))
    : String(segment ?? "").match(new RegExp(`(\\d+(?:\\.\\d+)?)\\s*${u}`, "i"));
  return m ? m[1] : "";
};

// Parts are joined with " | " (see combineCompoundAnswer) so each box's value is isolated by
// position, not re-derived by scanning the whole string per unit — a whole-string scan let the
// pounds digits (followed by whitespace, then eventually "p") get matched into the pence box too,
// duplicating the value and making the pence box un-erasable. Old stored answers predating the
// delimiter fall back to the whole-string scan.
export const parseCompoundParts = (value, units) => {
  const raw = String(value ?? "");
  if (!raw) return units.map(() => "");
  const segments = raw.split("|");
  if (segments.length === units.length) {
    return segments.map((seg, i) => matchUnitValue(seg, units[i]));
  }
  return units.map((unit) => matchUnitValue(raw, unit));
};

export const combineCompoundAnswer = (values, units) => {
  // All boxes empty is no answer at all — joining the bare unit labels ("£ p") would submit as one.
  if (values.every((v) => String(v ?? "").trim() === "")) return "";
  return values
    .map((v, i) => (CURRENCY_UNITS.includes(units[i]) ? `${units[i]}${v}` : `${v} ${units[i]}`))
    .join(" | ");
};
